import { IonIcon } from "@ionic/react";
import { chevronForward } from "ionicons/icons";
import { FC, ReactNode } from "react";
import styled, { css } from "styled-components";
import { formatDeadline, getUrgency, URGENCY_COLORS } from "./urgency";

/**
 * Shared primitives of the Suivi dashboard (home + group space + call sheet; the
 * subject Calendrier borrows the list primitives). House style: cards floating on
 * a softly tinted canvas, inset hairline separators, pill buttons, status as dots
 * + words or small tinted tags — never filled banners. Color budget: warm
 * secondary appears ONLY on "je saisis" CTAs; call actions are outline pills;
 * semantic tints (success / danger / info) stay on small tags and icons.
 * Everything reads the study theme tokens, so a researcher THEME restyles it.
 */

const TEXT_RGB = "var(--app-color-text-rgb, 15, 23, 42)";
const PRIMARY_RGB = "var(--app-color-primary-rgb, 37, 99, 235)";
/** Softened text ink / chrome-color wash at the given opacity. */
const ink = (alpha: number): string => `rgba(${TEXT_RGB}, ${alpha})`;
const brand = (alpha: number): string => `rgba(${PRIMARY_RGB}, ${alpha})`;
const PRIMARY = "var(--app-color-primary, #2563eb)";
const TEXT = "var(--app-color-text, #0f172a)";

/**
 * Page canvas of the Suivi pages: the theme background under a soft glow of the
 * chrome color (so the header wave flows into the page) plus a faint wash that
 * lets the cards float instead of sitting white-on-white. Opaque composite — the
 * base color is the last layer: a bare translucent value lets the LEAVING page
 * show through Ionic route transitions (see theme/pageStyle.ts). No fixed
 * attachment (iOS Safari repaint hazard).
 */
export const SUIVI_CANVAS = [
  `radial-gradient(130% 420px at 50% -140px, ${brand(0.16)}, ${brand(0)} 75%)`,
  `linear-gradient(${brand(0.035)}, ${brand(0.035)})`,
  "var(--app-color-background, #ffffff)",
].join(", ");

/** Semantic tones for tags, icon tiles and status icons. Fixed hues for
 *  success/danger (meaning must not change with the theme); info rides the
 *  chrome color, neutral the text ink. */
export type SuiviTone = "neutral" | "info" | "success" | "danger";

const TONES: Record<SuiviTone, { ink: string; wash: string; tile: string }> = {
  neutral: { ink: ink(0.62), wash: ink(0.055), tile: ink(0.06) },
  info: { ink: PRIMARY, wash: brand(0.09), tile: brand(0.11) },
  success: { ink: "#15803d", wash: "rgba(22, 163, 74, 0.1)", tile: "rgba(22, 163, 74, 0.12)" },
  danger: { ink: "#b91c1c", wash: "rgba(220, 38, 38, 0.08)", tile: "rgba(220, 38, 38, 0.1)" },
};

/** Keyboard focus ring shared by every interactive Suivi element. */
const focusRing = css`
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${brand(0.35)};
  }
`;

export const Page = styled.div<{ $wide?: boolean }>`
  padding: 16px 20px 96px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: ${({ $wide }) => ($wide ? "1120px" : "720px")};
  margin: 0 auto;
  width: 100%;
`;

/**
 * Two stacks side by side on wide screens (hospital desktops), one column on
 * phones. DOM order = the phone reading order, so each Column keeps its sections
 * in the contract order and nothing moves on mobile.
 */
export const Columns = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (min-width: 1000px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

/** Quiet uppercase label (the Calendrier's month headers). Suivi sections use
 *  SectionHeader instead. */
export const SectionLabel = styled.p`
  margin: 6px 2px 0;
  font-size: 11.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${ink(0.55)};
`;

const SectionHeaderWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 2px -2px;
  min-height: 30px;
`;

const SectionGlyph = styled.span`
  width: 30px;
  height: 30px;
  border-radius: 10px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${brand(0.11)};
  color: ${PRIMARY};

  ion-icon {
    font-size: 17px;
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 16.5px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.005em;
  color: ${TEXT};
`;

export const CountBadge = styled.span`
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: ${ink(0.07)};
  color: ${ink(0.7)};
`;

/** Section heading: theme-tinted glyph + title (a real heading, so screen readers
 *  can jump between sections) + optional count badge. */
export const SectionHeader: FC<{ title: string; icon: string; count?: number }> = ({
  title,
  icon,
  count,
}) => (
  <SectionHeaderWrap>
    <SectionGlyph aria-hidden="true">
      <IonIcon icon={icon} />
    </SectionGlyph>
    <SectionTitle>{title}</SectionTitle>
    {count != null && <CountBadge>{count}</CountBadge>}
  </SectionHeaderWrap>
);

/** Card surface: theme surface, hairline edge (keeps the card defined even where
 *  the canvas is plain white), and a soft two-layer shadow. */
export const cardElevation = css`
  background: var(--app-color-surface-tint, #ffffff);
  border: 1px solid ${ink(0.07)};
  border-radius: 20px;
  box-shadow:
    0 1px 2px ${ink(0.04)},
    0 14px 30px -20px ${ink(0.28)};
`;

export const ListCard = styled.div`
  ${cardElevation}
  overflow: hidden;
`;

/**
 * Inset hairline between two sibling rows, stopping 16px short of the card edges.
 * Painted as a background image on a PURE `& + &` selector: styled-components v6
 * only rewrites the self-reference to the static component class when the
 * selector ends with `&` — a `& + &::before` stays bound to the per-props class,
 * so a tappable row next to a static one (Calendrier) lost its separator. Row
 * states therefore set background-COLOR only, never the shorthand.
 */
export const insetSeparator = css`
  & + & {
    background-image: linear-gradient(${ink(0.07)}, ${ink(0.07)});
    background-size: calc(100% - 32px) 1px;
    background-position: 16px 0;
    background-repeat: no-repeat;
  }
`;

/** Chevron of tappable rows (icon, not a text glyph — crisp at any font). */
const ChevronGlyph = styled(IonIcon)`
  flex: none;
  font-size: 17px;
  color: ${ink(0.28)};
  transition:
    transform 0.18s ease,
    color 0.18s ease;
`;

export const Chevron: FC = () => <ChevronGlyph icon={chevronForward} aria-hidden="true" />;

/** Disclosure chevron of a folded card (« À jour », « Historique »). */
export const FoldGlyph = styled(IonIcon)<{ $open: boolean }>`
  flex: none;
  font-size: 18px;
  color: ${ink(0.4)};
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  transition: transform 0.2s ease;
`;

export const Row = styled.div<{ $tappable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  min-height: 64px;
  width: 100%;
  text-align: left;
  background-color: transparent;
  border: none;
  font-family: inherit;
  color: inherit;
  cursor: ${({ $tappable }) => ($tappable ? "pointer" : "default")};
  -webkit-tap-highlight-color: transparent;
  transition: background-color 0.15s ease;

  ${insetSeparator}

  ${({ $tappable }) =>
    $tappable &&
    css`
      &:active {
        background-color: ${brand(0.06)};
      }

      @media (hover: hover) {
        &:hover {
          background-color: ${brand(0.035)};
        }

        &:hover ${ChevronGlyph} {
          transform: translateX(2px);
          color: ${PRIMARY};
        }
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset 0 0 0 2px ${brand(0.45)};
      }
    `}
`;

export const RowMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RowTitle = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  color: ${TEXT};
  overflow-wrap: anywhere;
`;

/** Secondary line(s). Wraps instead of clipping: a tag cut to « Cons… » is
 *  information lost, and phones are exactly where it happened. */
export const RowSub = styled.div`
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.35;
  color: ${ink(0.62)};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px 8px;
  overflow-wrap: anywhere;
`;

/** Quiet « · » between two plain facts of a RowSub (decorative, not read out). */
export const MetaSep = styled.span.attrs({ "aria-hidden": true })`
  color: ${ink(0.3)};

  &::before {
    content: "·";
  }
`;

export const RowEnd = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex: none;
`;

/** Leading square tile — the "what kind of work" glyph on list rows. */
export const IconTile = styled.span<{ $tone?: SuiviTone }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tone = "info" }) => TONES[$tone].tile};
  color: ${({ $tone = "info" }) => TONES[$tone].ink};

  ion-icon {
    font-size: 20px;
  }
`;

/** Small round status mark (history rows, reference-date rows). */
export const StatusMark = styled.span<{ $tone?: SuiviTone }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tone = "neutral" }) => TONES[$tone].tile};
  color: ${({ $tone = "neutral" }) => TONES[$tone].ink};

  ion-icon {
    font-size: 16px;
  }
`;

/** Deadline chip: colored dot + relative text — never a filled background. The
 *  soft halo on warn/hot adds presence without breaking that rule. */
const DueChipWrap = styled.span<{ $text: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $text }) => $text};
  white-space: nowrap;
`;

const DueDot = styled.i<{ $dot: string; $halo: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $dot }) => $dot};
  box-shadow: 0 0 0 3px ${({ $halo }) => $halo};
  display: inline-block;
  flex: none;
`;

export const DueChip: FC<{ endDate: string; t: (key: string, options?: Record<string, unknown>) => string }> = ({
  endDate,
  t,
}) => {
  const urgency = getUrgency(endDate);
  const colors = URGENCY_COLORS[urgency];
  return (
    <DueChipWrap $text={colors.text}>
      <DueDot $dot={colors.dot} $halo={colors.halo} />
      {formatDeadline(t, endDate)}
    </DueChipWrap>
  );
};

/** Status as words with a small dot (grey = not started, accent = in progress). */
export const StatusText = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? TEXT : ink(0.6))};

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex: none;
    background: ${({ $active }) =>
      $active ? "var(--app-color-emphasis, #2563eb)" : ink(0.25)};
  }
`;

const pillBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 999px;
  white-space: nowrap;
  font-family: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.1s ease;

  &:active {
    transform: scale(0.97);
  }

  ion-icon {
    flex: none;
  }

  ${focusRing}
`;

/** Warm CTA — "je saisis". The ONLY place the secondary color appears. */
export const FillPill = styled.button`
  ${pillBase}
  border: none;
  min-height: 36px;
  padding: 0 16px;
  font-size: 13.5px;
  font-weight: 700;
  background: var(--app-color-secondary, #f59e0b);
  color: var(--app-color-secondary-contrast, #231a02);
  box-shadow: 0 6px 14px -8px rgba(var(--app-color-secondary-rgb, 245, 158, 11), 0.9);

  ion-icon {
    font-size: 16px;
  }

  &:active {
    background: var(--app-color-secondary-shade, #d9880a);
  }
`;

/** Outline pill — "je téléphone" (and other quiet actions). */
export const OutlinePill = styled.button`
  ${pillBase}
  border: 1px solid ${brand(0.38)};
  min-height: 34px;
  padding: 0 13px;
  font-size: 13px;
  font-weight: 600;
  background: var(--app-color-surface-tint, #ffffff);
  color: ${PRIMARY};

  ion-icon {
    font-size: 15px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${brand(0.06)};
    }
  }

  &:active {
    background: ${brand(0.1)};
  }
`;

/** Tonal pill — a real but secondary action (« Nouveau dossier »). */
export const TonalPill = styled.button`
  ${pillBase}
  border: none;
  min-height: 44px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 700;
  background: ${brand(0.1)};
  color: ${PRIMARY};

  ion-icon {
    font-size: 18px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${brand(0.15)};
    }
  }

  &:active {
    background: ${brand(0.2)};
  }
`;

/** Text action in a card footer (« Ajouter un membre »…). */
export const CardAction = styled.button<{ $muted?: boolean }>`
  ${pillBase}
  border: none;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 600;
  background: transparent;
  color: ${({ $muted }) => ($muted ? ink(0.6) : PRIMARY)};

  ion-icon {
    font-size: 17px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $muted }) => ($muted ? ink(0.05) : brand(0.07))};
    }
  }

  &:active {
    background: ${({ $muted }) => ($muted ? ink(0.08) : brand(0.1))};
  }
`;

export const CardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  /* 4px + the action's 12px inset = the 16px avatar column above. */
  padding: 6px 4px;
  border-top: 1px solid ${ink(0.07)};
  background: ${ink(0.018)};
`;

/** Round icon button (member ⋯ menu). */
export const IconButton = styled.button`
  ${pillBase}
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${ink(0.45)};

  ion-icon {
    font-size: 18px;
  }

  @media (hover: hover) {
    &:hover {
      background: ${ink(0.06)};
      color: ${ink(0.7)};
    }
  }

  &:active {
    background: ${ink(0.1)};
  }
`;

/** Small fact tag. Tone carries meaning (granted / refused / to do); the default
 *  neutral is the quiet grey of plain facts (« Invité le … »). Long tags wrap
 *  inside themselves rather than being clipped. */
export const QuietTag = styled.span<{ $tone?: SuiviTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ $tone = "neutral" }) => TONES[$tone].ink};
  background: ${({ $tone = "neutral" }) => TONES[$tone].wash};
  border-radius: 8px;
  padding: 3px 8px;

  ion-icon {
    flex: none;
    font-size: 13px;
  }
`;

export const MemberInitial = styled.span<{ $muted?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--app-font-heading, inherit);
  font-weight: 700;
  font-size: 15px;
  /* Tinted from the chrome color, never the surface token: a white surface
     theme turned the old surface-colored circle invisible. */
  background: linear-gradient(145deg, ${brand(0.18)}, ${brand(0.08)});
  color: ${PRIMARY};
  box-shadow: inset 0 0 0 1px ${brand(0.12)};

  ${({ $muted }) =>
    $muted &&
    css`
      background: ${ink(0.06)};
      color: ${ink(0.42)};
      box-shadow: inset 0 0 0 1px ${ink(0.06)};
    `}
`;

export const EmptyText = styled.p`
  text-align: center;
  padding: 32px 20px;
  font-size: 15px;
  color: ${ink(0.6)};
`;

const EmptyWrap = styled.div<{ $card?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: ${({ $card }) => ($card ? "26px 20px" : "40px 20px")};
  text-align: center;
  ${({ $card }) => $card && cardElevation}
`;

const EmptyMark = styled.span<{ $tone: SuiviTone }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ $tone }) => TONES[$tone].tile};
  color: ${({ $tone }) => TONES[$tone].ink};
  box-shadow: 0 0 0 8px ${({ $tone }) => TONES[$tone].wash};

  ion-icon {
    font-size: 26px;
  }
`;

const EmptyMessage = styled.p`
  margin: 4px 0 0;
  max-width: 320px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: ${ink(0.7)};
`;

/** Friendly empty state: glyph in a haloed disc + one sentence. */
export const EmptyState: FC<{
  icon: string;
  tone?: SuiviTone;
  card?: boolean;
  children: ReactNode;
}> = ({ icon, tone = "neutral", card, children }) => (
  <EmptyWrap $card={card}>
    <EmptyMark $tone={tone} aria-hidden="true">
      <IonIcon icon={icon} />
    </EmptyMark>
    <EmptyMessage>{children}</EmptyMessage>
  </EmptyWrap>
);

/** One quiet sentence — future windows are never cards (design contract). */
export const FooterLine = styled.p`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.45;
  color: ${ink(0.58)};

  ion-icon {
    flex: none;
    margin-top: 1px;
    font-size: 15px;
    color: ${ink(0.4)};
  }
`;

export const CaptionLine = styled.p`
  margin: 2px 2px 0;
  font-size: 13.5px;
  color: ${ink(0.6)};

  b {
    color: ${TEXT};
    font-weight: 600;
  }
`;

/**
 * Ordinal-aware role label: with two+ same-role members (Outborn multiples), the
 * bare label is ambiguous — they become « Enfant 1 », « Enfant 2 » in creation
 * order. The rank comes from the SERVER (sibling_rank/sibling_count, computed
 * over the full family so every staff member numbers the same child identically,
 * whatever their site scope); the positional client computation is only a
 * fallback for payloads without the fields. KNOWN LIMIT: ranks are positional,
 * not persisted — removing an earlier data-empty sibling re-ranks the survivors
 * (which may already carry data). The fiche-collected name takes over as the
 * durable identifier as soon as it exists.
 */
export const memberRoleOrdinalLabel = (
  member: {
    user_id: number;
    study_role_id: number;
    role_label: string | null;
    sibling_rank?: number | null;
    sibling_count?: number | null;
  },
  allMembers: { user_id: number; study_role_id: number }[],
  fallback: string
): string => {
  const base = member.role_label ?? fallback;
  if (member.sibling_rank != null && member.sibling_count != null) {
    return member.sibling_count >= 2 ? `${base} ${member.sibling_rank}` : base;
  }
  const siblings = allMembers
    .filter((candidate) => candidate.study_role_id === member.study_role_id)
    .sort((first, second) => first.user_id - second.user_id);
  if (siblings.length < 2) return base;
  const rank = siblings.findIndex((candidate) => candidate.user_id === member.user_id) + 1;
  return rank > 0 ? `${base} ${rank}` : base;
};
